import React, { useState } from 'react';
import { Button, Card, Col, Input, Row, Table, message, Select, InputNumber, DatePicker } from 'antd';
import { SearchOutlined, SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';

interface PaymentEditable {
  id_pago: number;
  id_procedimiento: number;
  contribuyente: string;
  referencia: string;
  monto: number;
  fecha_de_pago: string;
  fecha_de_aprobacion: string | null;
  metodo_pago: string;
  id_banco: number | null;
  banco: string | null;
  id_banco_destino: number | null;
  banco_destino: string | null;
}

interface Catalogs {
  metodos_pago: string[];
  bancos: { id_banco: number; nombre: string }[];
}

interface UpdatePagoPayload {
  monto?: number;
  fecha_de_pago?: string;
  referencia?: string;
  fecha_de_aprobacion?: string;
  metodo_pago?: string;
  id_banco?: number | null;
  id_banco_destino?: number | null;
}

const ModifyPayments: React.FC = () => {
  const [searching, setSearching] = useState(false);
  const [payments, setPayments] = useState<PaymentEditable[]>([]);
  const [catalogs, setCatalogs] = useState<Catalogs>({ metodos_pago: [], bancos: [] });
  const [numeroSolicitud, setNumeroSolicitud] = useState('');
  const [editing, setEditing] = useState<{ [id_pago: number]: Partial<PaymentEditable> }>({});

  const selectReadableDropdownProps = {
    dropdownMatchSelectWidth: 260,
  };

  const isNAValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    return String(value).trim().toUpperCase() === 'N/A';
  };

  const isEmptyOrNA = (value: unknown) => {
    if (value === null || value === undefined) return true;
    const normalized = String(value).trim();
    return normalized === '' || normalized.toUpperCase() === 'N/A';
  };

  const validateDateValue = (value: string, fieldLabel: string) => {
    if (isNAValue(value) || String(value).trim() === '') {
      throw new Error(`${fieldLabel} inválida`);
    }
    if (!moment(value, 'YYYY-MM-DD', true).isValid()) {
      throw new Error(`${fieldLabel} inválida. Formato esperado: YYYY-MM-DD`);
    }
  };

  const buildUpdatePayload = (original: PaymentEditable, edited: Partial<PaymentEditable>): UpdatePagoPayload => {
    const payload: UpdatePagoPayload = {};

    if (edited.referencia !== undefined) {
      const referencia = String(edited.referencia).trim();
      if (isNAValue(referencia)) throw new Error('Referencia inválida');
      if (referencia !== original.referencia) payload.referencia = referencia;
    }

    if (edited.monto !== undefined) {
      const monto = Number(edited.monto);
      if (Number.isNaN(monto) || monto <= 0) throw new Error('Monto inválido');
      if (monto !== Number(original.monto)) payload.monto = monto;
    }

    if (edited.fecha_de_pago !== undefined) {
      const fechaPago = String(edited.fecha_de_pago).trim();
      validateDateValue(fechaPago, 'Fecha de pago');
      if (fechaPago !== original.fecha_de_pago) payload.fecha_de_pago = fechaPago;
    }

    if (edited.fecha_de_aprobacion !== undefined) {
      const fechaAprobacion = String(edited.fecha_de_aprobacion).trim();
      validateDateValue(fechaAprobacion, 'Fecha de aprobación');
      if (fechaAprobacion !== original.fecha_de_aprobacion) payload.fecha_de_aprobacion = fechaAprobacion;
    }

    if (edited.metodo_pago !== undefined) {
      const metodo = String(edited.metodo_pago).trim();
      if (isEmptyOrNA(metodo)) throw new Error('Método de pago inválido');
      if (metodo !== original.metodo_pago) payload.metodo_pago = metodo;
    }

    if (edited.id_banco !== undefined) {
      if (edited.id_banco === null) {
        if (original.id_banco !== null) payload.id_banco = null;
      } else {
      const idBanco = Number(edited.id_banco);
      if (isNAValue(edited.id_banco)) throw new Error('Banco Origen inválido');
      if (!Number.isInteger(idBanco) || idBanco <= 0) throw new Error('Banco Origen inválido');
      if (idBanco !== original.id_banco) payload.id_banco = idBanco;
      }
    }

    if (edited.id_banco_destino !== undefined) {
      if (edited.id_banco_destino === null) {
        if (original.id_banco_destino !== null) payload.id_banco_destino = null;
      } else {
      const idBancoDestino = Number(edited.id_banco_destino);
      if (isNAValue(edited.id_banco_destino)) throw new Error('Banco Destino inválido');
      if (!Number.isInteger(idBancoDestino) || idBancoDestino <= 0) throw new Error('Banco Destino inválido');
      if (idBancoDestino !== original.id_banco_destino) payload.id_banco_destino = idBancoDestino;
      }
    }

    return payload;
  };

  const handleSearch = async (searchTerm?: string) => {
    const query = (searchTerm ?? numeroSolicitud).trim();
    if (!query) {
      message.warning('Debe ingresar número de solicitud o referencia');
      return;
    }
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/utilidades/pagos/search`, {
        params: { numero_solicitud: query },
        withCredentials: false,
        headers: { Authorization: `Bearer ${token}` }
      });
  // ...
      // Mapear pagos a la interfaz esperada
      const pagos: PaymentEditable[] = (res.data.pagos || []).map((p: any) => ({
        id_pago: p.id_pago,
        id_procedimiento: p.id_procedimiento,
        contribuyente: p.contribuyente || '',
        referencia: p.referencia,
        monto: typeof p.monto === 'string' ? parseFloat(p.monto) : p.monto,
        fecha_de_pago: p.fecha_de_pago,
        fecha_de_aprobacion: p.fecha_de_aprobacion || null,
        metodo_pago: p.metodo_pago,
        id_banco: p.id_banco ?? null,
        banco: p.banco ?? null,
        id_banco_destino: p.id_banco_destino ?? null,
        banco_destino: p.banco_destino ?? null
      }));
  // ...
      // Descarta cambios locales sin guardar para mostrar siempre el estado real del backend tras buscar.
      setEditing({});
      setPayments(pagos);
      setCatalogs({ bancos: res.data.bancos, metodos_pago: res.data.metodos_pago });
      if (pagos.length === 0) {
        message.info('No se encontraron pagos para la búsqueda.');
      }
    } catch (e) {
      // @ts-ignore
      message.error(e?.response?.data?.message || 'Error al buscar pagos');
    } finally {
      setSearching(false);
    }
  };

  const handleEdit = (id_pago: number) => async () => {
    // Buscar el pago original
    const original = payments.find(p => p.id_pago === id_pago);
    if (!original) {
      message.error('No se encontró el pago a editar');
      return;
    }

    const edited = editing[id_pago] || {};
    let payload: UpdatePagoPayload;
    try {
      payload = buildUpdatePayload(original, edited);
    } catch (validationError) {
      const err = validationError as Error;
      message.warning(err.message || 'Datos inválidos para actualizar');
      return;
    }

    if (Object.keys(payload).length === 0) {
      message.info('Sin cambios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/utilidades/pagos/${id_pago}`,
        payload,
        {
          withCredentials: false,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const responseMessage = response?.data?.message;
      if (responseMessage === 'Sin cambios') {
        message.info('Sin cambios');
      } else {
        message.success('Pago actualizado correctamente');
      }
      setEditing(ed => {
        const next = { ...ed };
        delete next[id_pago];
        return next;
      });
      // Si se actualizó la referencia, refresca usando la nueva para no perder el registro.
      const nextSearchTerm = payload.referencia || numeroSolicitud;
      if (nextSearchTerm !== numeroSolicitud) {
        setNumeroSolicitud(nextSearchTerm);
      }
      // Refresca la búsqueda para mostrar los datos actualizados
      handleSearch(nextSearchTerm);
    } catch (e) {
      const err = e as any;
      message.error(err?.response?.data?.message || 'Error al actualizar pago');
    }
  };

  const columns = [
    { title: 'ID Pago', dataIndex: 'id_pago', key: 'id_pago' },
    { title: 'Contribuyente', dataIndex: 'contribuyente', key: 'contribuyente' },
    { title: 'Referencia', dataIndex: 'referencia', key: 'referencia',
      render: (_: string, record: PaymentEditable) => (
        <Input
          value={editing[record.id_pago]?.referencia ?? record.referencia ?? ''}
          onChange={e => {
            const value = e.target.value ?? '';
            setEditing(ed => ({ ...ed, [record.id_pago]: { ...ed[record.id_pago], referencia: value } }));
          }}
        />
      )
    },
    { title: 'Monto', dataIndex: 'monto', key: 'monto',
      render: (_: number, record: PaymentEditable) => (
        <InputNumber
          value={editing[record.id_pago]?.monto ?? record.monto ?? 0}
          min={0}
          onChange={val => setEditing(ed => ({ ...ed, [record.id_pago]: { ...ed[record.id_pago], monto: val as number } }))}
        />
      )
    },
    { title: 'Fecha de Pago', dataIndex: 'fecha_de_pago', key: 'fecha_de_pago',
      render: (_: string, record: PaymentEditable) => (
        <DatePicker
          value={editing[record.id_pago]?.fecha_de_pago ? moment(editing[record.id_pago]?.fecha_de_pago) : (record.fecha_de_pago ? moment(record.fecha_de_pago) : undefined)}
          format="YYYY-MM-DD"
          allowClear={false}
          onChange={date => setEditing(ed => ({ ...ed, [record.id_pago]: { ...ed[record.id_pago], fecha_de_pago: date?.format('YYYY-MM-DD') || '' } }))}
        />
      )
    },
    { title: 'Fecha de Aprobación', dataIndex: 'fecha_de_aprobacion', key: 'fecha_de_aprobacion',
      render: (_: string | null, record: PaymentEditable) => (
        <DatePicker
          value={editing[record.id_pago]?.fecha_de_aprobacion ? moment(editing[record.id_pago]?.fecha_de_aprobacion ?? undefined) : (record.fecha_de_aprobacion ? moment(record.fecha_de_aprobacion ?? undefined) : undefined)}
          format="YYYY-MM-DD"
          allowClear={false}
          onChange={date => setEditing(ed => ({ ...ed, [record.id_pago]: { ...ed[record.id_pago], fecha_de_aprobacion: date?.format('YYYY-MM-DD') || '' } }))}
        />
      )
    },
    { title: 'Método de Pago', dataIndex: 'metodo_pago', key: 'metodo_pago',
      render: (_: string, record: PaymentEditable) => (
        <Select
          {...selectReadableDropdownProps}
          value={editing[record.id_pago]?.metodo_pago ?? record.metodo_pago ?? undefined}
          style={{ width: 180 }}
          onChange={val => setEditing(ed => ({ ...ed, [record.id_pago]: { ...ed[record.id_pago], metodo_pago: val } }))}
        >
          {catalogs.metodos_pago.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
        </Select>
      )
    },
    { title: 'Banco Origen', dataIndex: 'id_banco', key: 'id_banco',
      render: (_: number | null, record: PaymentEditable) => (
        (() => {
          const hasEditedValue = Object.prototype.hasOwnProperty.call(editing[record.id_pago] || {}, 'id_banco');
          const selectedValue = hasEditedValue
            ? (editing[record.id_pago]?.id_banco ?? undefined)
            : (record.id_banco ?? undefined);
          return (
        <Select
          {...selectReadableDropdownProps}
          value={selectedValue}
          style={{ width: 180 }}
          allowClear
          placeholder='Seleccione banco origen'
          onChange={val => setEditing(ed => ({
            ...ed,
            [record.id_pago]: {
              ...ed[record.id_pago],
              id_banco: val === undefined ? null : val
            }
          }))}
        >
          {catalogs.bancos.map(b => <Select.Option key={b.id_banco} value={b.id_banco}>{b.nombre}</Select.Option>)}
        </Select>
          );
        })()
      )
    },
    { title: 'Banco Destino', dataIndex: 'id_banco_destino', key: 'id_banco_destino',
      render: (_: number | null, record: PaymentEditable) => (
        (() => {
          const hasEditedValue = Object.prototype.hasOwnProperty.call(editing[record.id_pago] || {}, 'id_banco_destino');
          const selectedValue = hasEditedValue
            ? (editing[record.id_pago]?.id_banco_destino ?? undefined)
            : (record.id_banco_destino ?? undefined);
          return (
        <Select
          {...selectReadableDropdownProps}
          value={selectedValue}
          style={{ width: 180 }}
          allowClear
          placeholder='Seleccione banco destino'
          onChange={val => setEditing(ed => ({
            ...ed,
            [record.id_pago]: {
              ...ed[record.id_pago],
              id_banco_destino: val === undefined ? null : val
            }
          }))}
        >
          {catalogs.bancos.map(b => <Select.Option key={b.id_banco} value={b.id_banco}>{b.nombre}</Select.Option>)}
        </Select>
          );
        })()
      )
    },
    { title: 'Guardar', key: 'guardar',
      render: (_: any, record: PaymentEditable) => (
        <Button icon={<SaveOutlined />} type="primary" onClick={handleEdit(record.id_pago)} />
      )
    }
  ];

  return (
  <Card bodyStyle={{ padding: 0, overflowX: 'auto' }} style={{ overflowX: 'auto' }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input
            placeholder="Número de solicitud o referencia"
            value={numeroSolicitud}
            onChange={e => setNumeroSolicitud(e.target.value)}
            onPressEnter={() => handleSearch()}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={searching}
            onClick={() => handleSearch()}
          >
            Buscar Pagos
          </Button>
        </Col>
      </Row>
      <div style={{ width: '100vw', minWidth: 900, overflowX: 'auto' }}>
        <Table
          dataSource={payments}
          columns={columns}
          rowKey="id_pago"
          pagination={false}
          locale={{ emptyText: 'Sin resultados' }}
          scroll={{ x: Math.max(window.innerWidth, 1200) }}
        />
      </div>
    </Card>
  );
};

export default ModifyPayments;
